import { useEffect } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, Lock, Star, Landmark } from '@/components/ui/icons'
import { FONTS, SPACING, RADIUS, COLORS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { RETENTION_EVENTS } from '@/lib/retention/events'

interface CoverageStayReasonCardProps {
  compact?: boolean
  testID?: string
}

const POINTS = [
  { icon: ShieldCheck, key: 'retention.coverage_point_insurance' },
  { icon: Lock, key: 'retention.coverage_point_escrow' },
  { icon: Star, key: 'retention.coverage_point_reputation' },
  { icon: Landmark, key: 'retention.coverage_point_payments' },
] as const

export function CoverageStayReasonCard({
  compact = false,
  testID = 'retention.coverage-stay',
}: CoverageStayReasonCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  useEffect(() => {
    productTelemetryService.recordEvent('booking_step', RETENTION_EVENTS.coverageValueViewed, {
      elementId: testID,
    })
  }, [testID])

  return (
    <View
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={t('retention.coverage_title')}
      style={{
        padding: compact ? SPACING.sm : SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        gap: SPACING.sm,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {t('retention.coverage_kicker')}
      </Text>
      <Text
        style={{
          fontSize: 17,
          lineHeight: compact ? 22 : 24,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
        }}
      >
        {t('retention.coverage_title')}
      </Text>
      <Text
        style={{
          fontSize: 14,
          lineHeight: 20,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
        }}
      >
        {t('retention.coverage_body')}
      </Text>
      <View style={{ gap: SPACING.sm }}>
        {POINTS.map((point) => {
          const Icon = point.icon
          return (
            <View
              key={point.key}
              style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}
            >
              <Icon size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              <Text
                style={{
                  flex: 1,
                  fontSize: 14,
                  lineHeight: 20,
                  fontFamily: FONTS.body,
                  color: colors.textPrimary,
                }}
              >
                {t(point.key)}
              </Text>
            </View>
          )
        })}
      </View>
    </View>
  )
}
